import { useParams } from 'react-router';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { Alert, Form } from 'antd';
import dayjs from 'dayjs';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import { ENROLL_PRECHARGEOFF_SETTLEMENT_OFFER } from 'mutations/prechargeoff';
import { SIZE, STATUS } from 'utilities/constants';

import StepContainer from '../../StepContainer';
import PrecoDisclosures from './PrecoDisclosures';
import {
  buildEnrollVariables,
  PRECO_ENROLLMENT_TYPES,
  PRECO_STEPS,
  resetPrecoState,
  setNextStep,
  usePrecoState,
} from './precoState';

const PrecoDisclosureStep = () => {
  const { customerId, accountId } = useParams();
  const client = useApolloClient();
  const { offer, enrollmentType } = usePrecoState();
  const [form] = Form.useForm();

  const isFlexiblePlan =
    enrollmentType === PRECO_ENROLLMENT_TYPES.FLEXIBLE_PLAN;

  const [
    enrollOffer,
    { loading: enrollLoading, data: enrollSuccess, error: enrollError },
  ] = useMutation(ENROLL_PRECHARGEOFF_SETTLEMENT_OFFER, {
    onError: () => {},
    onCompleted: () =>
      setTimeout(() => {
        client.refetchQueries({
          include: [
            'GetCurrentPrecoOffer',
            'GetCustomerWithFullAccounts',
            'AccountHistory',
          ],
        });
        resetPrecoState();
      }, 2500),
  });

  const onFinish = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (isFlexiblePlan) {
      enrollOffer({
        variables: buildEnrollVariables({ offer, customerId, accountId }),
      });
      return;
    }

    setNextStep(PRECO_STEPS.EFTA);
  };

  return (
    <StepContainer
      onContinue={onFinish}
      buttonText={isFlexiblePlan ? 'Enroll Offer' : 'Continue'}
      buttonProps={{
        disabled: enrollLoading,
        loading: enrollLoading,
        block: isFlexiblePlan,
      }}
    >
      <Form name="precoDisclosures" form={form} layout="vertical">
        <PrecoDisclosures
          offer={offer}
          outstandingBalance={offer.originalOutstandingBalance}
          expirationDate={dayjs(offer.expirationDate)}
        />
        {enrollError && (
          <Alert
            banner
            type="error"
            title="Error creating program. Please try again."
            style={{ marginTop: 'var(--spacing-sm)' }}
          />
        )}
        <SectionMessage
          cover={true}
          status={STATUS.SUCCESS}
          size={SIZE.MD}
          text="Program successfully created!"
          visible={!!enrollSuccess}
        />
      </Form>
    </StepContainer>
  );
};

export default PrecoDisclosureStep;
